import moment from "moment-timezone";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import useApi from "../../../../../utils/api";
import { useFilters } from "../../../../../utils/hooks/useFilters";
import type { AvailabilityRow } from "./types";

const DUMMY_RECURRING_DAYS = {
    sunday: { allDay: true, isAvailable: false },
    monday: { allDay: false, isAvailable: true, shifts: [{ from: "2024-01-01T09:00:00", to: "2024-01-01T17:00:00" }] },
    tuesday: { allDay: false, isAvailable: true, shifts: [{ from: "2024-01-02T09:00:00", to: "2024-01-02T17:00:00" }] },
    wednesday: { allDay: false, isAvailable: true, shifts: [{ from: "2024-01-03T09:00:00", to: "2024-01-03T17:00:00" }] },
    thursday: { allDay: false, isAvailable: true, shifts: [{ from: "2024-01-04T09:00:00", to: "2024-01-04T17:00:00" }] },
    friday: { allDay: false, isAvailable: true, shifts: [{ from: "2024-01-05T09:00:00", to: "2024-01-05T17:00:00" }] },
    saturday: { allDay: true, isAvailable: false, reason: "Weekend off" },
};

const DUMMY_TEMPORARY = [
    {
        day: moment().format("YYYY-MM-DD"),
        dateShift: { allDay: false, isAvailable: true, shifts: [{ from: moment().hour(9).minute(0).toISOString(), to: moment().hour(17).minute(0).toISOString() }] },
    },
    {
        day: moment().add(1, "day").format("YYYY-MM-DD"),
        dateShift: { allDay: true, isAvailable: false, reason: "Vacation" },
    },
    {
        day: moment().add(3, "day").format("YYYY-MM-DD"),
        dateShift: { allDay: false, isAvailable: true, shifts: [{ from: moment().add(3, "day").hour(12).minute(0).toISOString(), to: moment().add(3, "day").hour(20).minute(0).toISOString() }] },
    },
];

const DUMMY_AVAILABILITY: AvailabilityRow[] = [
    {
        name: "Arben Hoxha",
        user: { id: "user-1", firstName: "Arben", lastName: "Hoxha", employeeNumber: "EMP-1001" },
        unit: { id: "unit-1", code: "DT", description: "Downtown" },
    },
    {
        name: "Elira Krasniqi",
        user: { id: "user-2", firstName: "Elira", lastName: "Krasniqi", employeeNumber: "EMP-1002" },
        unit: { id: "unit-2", code: "AP", description: "Airport" },
    },
    {
        name: "Dritan Berisha",
        user: { id: "user-3", firstName: "Dritan", lastName: "Berisha", employeeNumber: "EMP-1003" },
        unit: { id: "unit-3", code: "RV", description: "Riverside" },
    },
    {
        name: "Vesa Shala",
        user: { id: "user-4", firstName: "Vesa", lastName: "Shala", employeeNumber: "EMP-1004" },
        unit: { id: "unit-1", code: "DT", description: "Downtown" },
    },
    {
        name: "Ilir Kelmendi",
        user: { id: "user-5", firstName: "Ilir", lastName: "Kelmendi", employeeNumber: "EMP-1005" },
        unit: { id: "unit-4", code: "UP", description: "Uptown" },
    },
];

export type AvailabilityParams =
    | { type: "recurring" }
    | { type: "temporary"; startDate: string; endDate: string };

// TODO: placeholder endpoint - swap out once the real one exists
export const useAvailability = (userId?: string, params?: AvailabilityParams) => {
    const { authGet } = useApi();

    const query = useQuery({
        queryKey: [`/users/${userId}/availability`, params],
        queryFn: ({ queryKey }: any) => {
            const [url, params] = queryKey;
            return authGet(url, { params });
        },
        enabled: false, // TODO: enable once the endpoint exists
        placeholderData: keepPreviousData,
    });

    const dummyData = params?.type === "temporary" ? DUMMY_TEMPORARY : { days: DUMMY_RECURRING_DAYS };

    return { ...query, data: query.data ?? dummyData };
};

// TODO: placeholder endpoint - swap out once the real one exists
export const useUsersAvailability = () => {
    const { authGet } = useApi();
    const [allFilters] = useFilters();

    const query = useQuery({
        queryKey: ["/users/availability", allFilters],
        queryFn: ({ queryKey }: any) => {
            const [url, params] = queryKey;
            return authGet(url, { params });
        },
        placeholderData: keepPreviousData,
        enabled: false, // TODO: enable once the endpoint exists
    });

    return {
        data: query.data?.data ?? DUMMY_AVAILABILITY,
        totalItems: query.data?.totalItems ?? DUMMY_AVAILABILITY.length,
        isLoading: query.isLoading,
    };
};
